import React, { useState, useEffect, Suspense, lazy } from "react";
import { Capacitor } from '@capacitor/core';
import "@copilotkit/react-ui/styles.css";
import "react-grid-layout/css/styles.css";
import "react-resizable/css/styles.css";
import MsalProvider from "./components/MsalProvider";
import {
  BrowserRouter,
  MemoryRouter,
  Routes,
  Route,
  Navigate,
  Outlet,
  useLocation,
  useNavigate,
} from "react-router-dom";
import { useTheme } from "./utils/theme";
import Header from "./components/Header";
import URITransferPortal from "./components/URITransferPortal";
import RosterList from "./components/RosterList";
import RosterProfile from "./components/RosterProfile";
import RosterAnalytics from "./components/RosterAnalytics";
// Hardcoded profile components removed in Phase 2 design cleanup
// Use dynamic routes: /player/:playerId or /team-player-profile/:playerId
// BiomechanicsAnalysis removed - Task 4 Page Removals
import { DevelopmentProjection } from "./components/development-projection";
import { Reports } from "./components/reports";
import Sidebar from "./components/Sidebar";
import ChatInterface from "./components/ChatInterface";
import Settings from "./components/Settings";
import SLAMSettings from "./components/settings/SLAMSettings";
import Permissions from "./components/Permissions";
// HowItWorks moved to AgentUniverse - Task 5 Getting Started Swap
// import HowItWorks from "./components/Dashboard";
import Scouting from "./components/Scouting";
import VideoAnalytics from "./components/VideoAnalytics";
import VideoAnalyticsPage from "./pages/VideoAnalyticsPage";
import VideoHubPage from "./pages/VideoHubPage";
import DropZonePage from "./pages/DropZonePage";
import SystemIntegrations from "./components/SystemIntegrations";
import PlayerList from "./components/PlayerList";
import TeamPlayerProfile from "./components/TeamPlayerProfile";
import TeamRecord from "./components/TeamRecord";
// MyDashboard removed - consolidated into main dashboard

// Hardcoded player profile components removed in Phase 2 design cleanup
// Use GeneralProfile (imported as PlayerProfile) for dynamic player data
import PlayerRoster from "./components/PlayerRoster";
import PlayerProfileDetail from "./components/PlayerProfileDetail";
import Sourcing from "./components/Sourcing";
import PlayerProfile from "./components/player_profiles/GeneralProfile";
import Login from "./components/Login";
import PasswordGate from "./components/auth/PasswordGate";
import "./App.css";
// NILValuationEngine removed - Task 4 Page Removals
import { PlayerDevelopment } from "./components/playerdevelopment";
import { getNavigator, setNavigator } from "./utils/navigation";
import { getAllSportsByUserId, getUser, getUserSettings, refreshToken, isValidReturnUrl, clearAuth } from "./utils/helpers";
import RecuritingPortal from "./components/RecuritingPortal";
// AskSLAMAI removed — resolved via COMPONENT_MAP + SPORT_ASK_AI in sportRoutes.js
import BaseballAskAI from "./pages/baseball/BaseballAskAI";
import TeamPlayerProfileNew from "./components/TeamPlayerProfileNew";
import MyPlayerProfile from "./components/MyPlayerProfile";

// Widget Framework removed — external widget-server is decommissioned

// Import V2 Components (SLAM V2 Integration)
// AgentUniverse removed — replaced by CoachingCanvas as default agent builder
import { VideoAnalyzer } from "./components/v2/analyzer";
import TeamSelector from "./components/TeamSelector";
import RosterWithTeamSelection from "./components/RosterWithTeamSelection";
import TheWireReport from "./components/wire/TheWireReport";
import PitchPredictionAnalysis from "./pages/PitchPredictionAnalysis";
import PitchAnalysisDashboardPage from "./pages/PitchTippingDashboardPage";

// Import NIL Dashboard Components
import PlayerNILCard from "./components/player-card/PlayerNILCard";

// Import Player Development Components
import AthletePerformance from "./components/AthletePerformance";
import HealthIntegrations from "./components/HealthIntegrations";

// Demo components removed — mock data endpoints deprecated

// Import Scouting Components

// Import StatCast Direct Query Components
import { StatcastAgentPanel } from "./components/statcast";

// Import Recruiting & Load Management Components (SLAM Ideas)
import { CatapultDashboard } from "./components/catapult";

// Import Strength & Wellness Pages
import {
  WorkoutGeneratorPage,
  NutritionDiaryPage,
  AICoachPage,
  SmartJournalPage,
  PublishCreatorPage,
  StrengthAdminPage,
  CrossFitGeneratorPage,
  WorkoutHistoryPage,
} from "./pages/strength";
import MobileAppEmulatorPage from "./pages/mobile/MobileAppEmulatorPage";

// Import Basketball Section Components (SLAM BALL)
import {
  BasketballDashboard,
  BasketballRosterManagement,
  BasketballPlayerPage,
  BasketballAnalytics,
  BasketballIntelligenceV2,
  BasketballAskAI,
  BasketballClipping,
  SlamMobile,
  WojChat,
  GMDashboard,
  PlayerDetail,
} from "./pages/basketball";

// Import Basketball Wire Report (The Wire for Basketball)
import { BasketballWireReport, BrownWireReport } from "./components/basketball-wire";

// Import Game Analysis Dashboards
import SpursOKCDashboard from "./pages/spurs-okc/SpursOKCDashboard";
import FloridaHoustonDashboard from "./pages/florida-houston/FloridaHoustonDashboard";
import RealMadridDashboard from "./pages/real-madrid/RealMadridDashboard";
import BrownHarvardDashboard from "./pages/basketball/BrownHarvardDashboard";
import MyLibraryPage from "./pages/basketball/MyLibraryPage";
import CS3sBiomechDashboard from "./pages/cs3s-biomech/CS3sBiomechDashboard";

// Import Workspace Components
import {
  WorkspaceProvider,
  WorkspaceSelector,
  WorkspaceDashboard,
} from "./components/workspace";
import { InvitationAcceptPage } from "./pages/InvitationAcceptPage";
import { TeamInviteAcceptPage } from "./pages/TeamInviteAcceptPage";
import BiometricsPage from "./pages/BiometricsPage";

// Import Baseball Section Components
import BaseballClipping from "./pages/baseball/BaseballClipping";

// Import UNC Baseball Components
import UNCRosterGrid from './components/unc/UNCRosterGrid';
import UNCPlayerCard from './components/unc/UNCPlayerCard';
import UNCGMDashboard from './pages/baseball/UNCGMDashboard';

// Import Sport-specific AskAI Components
import { GolfAskAI } from "./pages/golf";
import { SoccerAskAI } from "./pages/soccer";
import { HockeyAskAI } from "./pages/hockey";
import { FootballAskAI } from "./pages/football";
import { F1AskAI } from "./pages/f1";

// Import Unified Sport Layout + Route Config
import SportLayout from "./components/SportLayout";
import { SPORTS, getRoutesForSport } from "./config/sportRoutes";

// Import Coaching Canvas (Agent Builder)
import CoachingCanvas from "./components/canvas/CoachingCanvas";

// Import Progressive Disclosure (PLG Phase C)
import ReadinessGate from './components/auth/ReadinessGate';

// Import Post-Login Loader (loading animation after login)

// Import Role Configuration
import {
  ROLE_TYPES,
  ROLE_HIERARCHY,
  hasMinimumRole,
  canRoleAccessRoute,
  getDefaultDashboard,
  migrateRoleName,
} from "./config/roleConfig";

// Import Route Mappings for legacy redirects
import { ROUTE_MAPPINGS } from "./constants/userFacingLabels";
import { Toaster } from "./components/ui/toaster";

// Mobile endpoint — lazy-loaded to keep desktop bundle unaffected
const MobileApp = lazy(() => import('./m/MobileApp'));

// Standalone analyzer for Flutter WebView embedding (no nav chrome)
const StandaloneAnalyzer = lazy(() => import('./m/features/video/StandaloneAnalyzer'));

// PLG Trial Mode — lazy-loaded to avoid bloating authenticated bundle
const DemoShell = lazy(() => import('./components/auth/DemoShell'));
const MobileDemoShell = lazy(() => import('./m/features/MobileDemoShell'));
const DraftDay = lazy(() => import('./components/auth/DraftDay'));
const LockerRoom = lazy(() => import('./components/auth/LockerRoom'));
const MovementAnalysisPage = React.lazy(() => import('./pages/strength/MovementAnalysisPage'));

// ═══════════════════════════════════════════════════════════════════════════
// COMPONENT MAP — maps componentKey strings from sportRoutes.js to actual
// React components. This keeps the route config free of import dependencies.
// ═══════════════════════════════════════════════════════════════════════════
const COMPONENT_MAP = {
  CoachingCanvas,
  LockerRoom,
  GMDashboard,
  PlayerDetail,
  VideoAnalyticsPage,
  VideoHubPage,
  DropZonePage,
  MyLibraryPage,
  MobileAppEmulatorPage,
  SlamMobile,
  PlayerDevelopment,
  HealthIntegrations,
  TeamPlayerProfile,
  Scouting,
  CatapultDashboard,
  BiometricsPage,
  WorkoutGeneratorPage,
  NutritionDiaryPage,
  AICoachPage,
  SmartJournalPage,
  PublishCreatorPage,
  StrengthAdminPage,
  CrossFitGeneratorPage,
  WorkoutHistoryPage,
  MovementAnalysisPage,
  Reports,
  SystemIntegrations,
  Settings,
  SLAMSettings,
  Permissions,
  // Sport-specific AskAI
  BasketballAskAI,
  BaseballAskAI,
  GolfAskAI,
  SoccerAskAI,
  HockeyAskAI,
  FootballAskAI,
  F1AskAI,
  // Basketball-only
  BasketballDashboard,
  BasketballRosterManagement,
  BasketballPlayerPage,
  BasketballAnalytics,
  BasketballIntelligenceV2,
  BasketballWireReport,
  BrownWireReport,
  BrownHarvardDashboard,
  FloridaHoustonDashboard,
  RealMadridDashboard,
  WojChat,
  // Baseball-only
  UNCRosterGrid,
  UNCPlayerCard,
  UNCGMDashboard,
  TheWireReport,
  PitchAnalysisDashboardPage,
  PitchPredictionAnalysis,
  StatcastAgentPanel,
  BaseballClipping,
  // Generic roster (with sportType prop)
  RosterWithTeamSelection,
};

/**
 * Generate <Route> elements for a sport from the route config.
 * Resolves componentKeys to actual components via COMPONENT_MAP.
 */
function renderSportRoutes(sport) {
  const routes = getRoutesForSport(sport);
  return routes.map((routeDef) => {
    // Redirect routes
    if (routeDef.redirect) {
      return (
        <Route
          key={routeDef.path}
          path={routeDef.path}
          element={<Navigate to={`/${sport}/${routeDef.redirect}`} replace />}
        />
      );
    }

    const Component = COMPONENT_MAP[routeDef.componentKey];
    if (!Component) {
      console.warn(`[sportRoutes] Missing component: ${routeDef.componentKey} for ${sport}/${routeDef.path}`);
      return null;
    }

    let element = <Component {...(routeDef.props || {})} />;

    // Wrap in Suspense if needed (for lazy-loaded components)
    if (routeDef.suspense) {
      element = <Suspense fallback={null}>{element}</Suspense>;
    }

    // Wrap in ReadinessGate if needed
    if (routeDef.readinessGate) {
      element = <ReadinessGate>{element}</ReadinessGate>;
    }

    return <Route key={routeDef.path} path={routeDef.path} element={element} />;
  });
}

// Use MemoryRouter on native iOS to prevent swipe-to-go-back conflicts
const Router = Capacitor.isNativePlatform() ? MemoryRouter : BrowserRouter;

// Centralized authentication check
const isAuthenticated = () => {
  const user = getUser();
  return user && Object.keys(user).length > 0;
};

/**
 * Get user's role, handling legacy role migration
 * @returns {string} The user's role ID (e.g., 'ADMIN', 'HEAD_COACH')
 */
const getUserRole = () => {
  const user = getUser();
  if (!user?.role) return null;

  // If using old role name, migrate to new system
  if (!ROLE_HIERARCHY[user.role]) {
    return migrateRoleName(user.role);
  }
  return user.role;
};

// Centralized admin check - now checks for ADMIN role type
const isAdmin = () => {
  const role = getUserRole();
  return role === ROLE_TYPES.ADMIN;
};

/**
 * Check if user has at least the specified role level
 * @param {string} requiredRole - The minimum role required
 * @returns {boolean} Whether user has sufficient role level
 */
const userHasMinimumRole = (requiredRole) => {
  const role = getUserRole();
  if (!role) return false;
  return hasMinimumRole(role, requiredRole);
};

/**
 * Check if user's role can access a specific route
 * @param {string} route - The route path to check
 * @returns {boolean} Whether user can access the route
 */
const canUserAccessRoute = (route) => {
  const role = getUserRole();
  if (!role) return false;

  // Admin can access everything
  if (role === ROLE_TYPES.ADMIN) return true;

  return canRoleAccessRoute(role, route);
};

// Centralized route-access check - uses both legacy accessibleRoutes and new role system
const hasRouteAccess = (route) => {
  const user = getUser();

  // First check role-based access (new system)
  if (canUserAccessRoute(route)) {
    return true;
  }

  // Fall back to legacy accessibleRoutes array
  return (
    Array.isArray(user?.accessibleRoutes) &&
    user.accessibleRoutes.includes(route)
  );
};

// Protected Route component - saves intended URL for post-login redirect
const ProtectedRoute = ({ children }) => {
  const location = useLocation();
  if (!isAuthenticated()) {
    // Save the URL the user was trying to access
    sessionStorage.setItem('redirectAfterLogin', location.pathname + location.search);
    return <Navigate to="/login" replace />;
  }
  return children;
};

// Admin Route component - extends ProtectedRoute to also check for ADMIN role
const AdminRoute = ({ children }) => {
  const location = useLocation();

  // First ensure the user is authenticated — preserve returnUrl
  if (!isAuthenticated()) {
    const returnUrl = location.pathname + location.search + location.hash;
    return <Navigate to={`/login?returnUrl=${encodeURIComponent(returnUrl)}`} replace />;
  }

  // If user is admin, allow access to admin routes regardless of accessibleRoutes
  if (isAdmin()) {
    return children;
  }

  // If not admin, redirect to first accessible route
  try {
    const userData = sessionStorage.getItem("user");
    const parsedUser = JSON.parse(userData);
    const accessibleRoutes = parsedUser.accessibleRoutes || [];
    // Redirect to first accessible route instead of dashboard
    const redirectTo =
      accessibleRoutes.length > 0 ? accessibleRoutes[0] : "/no-access";

    return (
      <Navigate
        to={redirectTo}
        state={{
          unauthorized: true,
          message: "You do not have permission to access this page",
        }}
        replace
      />
    );
  } catch (error) {
    return <Navigate to="/no-access" replace />;
  }
};

// Default Route component - redirects based on onboarding state
const DefaultRoute = () => {
  const location = useLocation();
  if (!isAuthenticated()) {
    const returnUrl = location.pathname + location.search + location.hash;
    return <Navigate to={`/login?returnUrl=${encodeURIComponent(returnUrl)}`} replace />;
  }

  // Check if user completed sport onboarding (slam_sport_selected is ONLY set by TeamSelector or DraftDay)
  const hasSport = localStorage.getItem('slam_sport_selected') === 'true';
  if (!hasSport) {
    // New users go to DraftDay (gamified onboarding), not the plain sport picker
    return <Navigate to="/draft-day" replace />;
  }

  // Route to the user's selected sport canvas (agent builder)
  const sportSelection = localStorage.getItem('slam_sport_selection') || 'basketball';
  return <Navigate to={`/${sportSelection}/canvas`} replace />;
};

// Generic sport-aware redirect: reads user's last sport selection, appends subpath
const SportRedirect = ({ subpath = 'canvas' }) => {
  const sportSelection = localStorage.getItem('slam_sport_selection') || 'basketball';
  return <Navigate to={`/${sportSelection}/${subpath}`} replace />;
};

// Legacy /slam-ai redirect to sport-specific ask-slam-ai
const SlamAIRedirect = () => <SportRedirect subpath="ask-slam-ai" />;

// PLG Trial Mode — full-screen demo shell for unauthenticated users
const TryRoutes = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const handleSignUp = () => {
    // Map /try path back to the real auth path for post-login redirect
    const tryPath = location.pathname + location.search;
    const realPath = tryPath.replace(/^\/try/, '') || '/';
    sessionStorage.setItem('redirectAfterLogin', realPath);
    navigate('/login');
  };

  // If user is already authenticated, redirect to the real app
  const user = (() => { try { const r = sessionStorage.getItem('user'); return r ? JSON.parse(r) : null; } catch { return null; } })();
  if (user && Object.keys(user).length > 0) {
    const hasSport = localStorage.getItem('slam_sport_selected') === 'true';
    const sport = localStorage.getItem('slam_sport_selection') || 'basketball';
    return <Navigate to={hasSport ? `/${sport}/canvas` : '/select-sport'} replace />;
  }

  return <DemoShell onSignUp={handleSignUp} currentPath={location.pathname} />;
};

// PLG Mobile Trial Mode — mobile demo shell for unauthenticated users
const MobileTryRoutes = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const handleSignUp = () => {
    const tryPath = location.pathname + location.search;
    const realPath = tryPath.replace(/^\/m\/try/, '') || '/';
    sessionStorage.setItem('redirectAfterLogin', realPath);
    navigate('/login');
  };

  const user = (() => { try { const r = sessionStorage.getItem('user'); return r ? JSON.parse(r) : null; } catch { return null; } })();
  if (user && Object.keys(user).length > 0) {
    const hasSport = localStorage.getItem('slam_sport_selected') === 'true';
    const sport = localStorage.getItem('slam_sport_selection') || 'basketball';
    return <Navigate to={hasSport ? `/m/${sport}/canvas` : '/m/select-sport'} replace />;
  }

  return <MobileDemoShell onSignUp={handleSignUp} />;
};

// Legacy /ask-slam-ai redirect
const LegacyAskRedirect = () => <SportRedirect subpath="canvas" />;

// No Access component for users without any permissions
const NoAccessPage = () => {
  const { theme } = useTheme();
  const navigate = useNavigate();
  const isDarkMode = true; // OLED: always dark

  const handleLogout = () => {
    clearAuth();
    navigate("/login");
  };

  return (
    <div
      className="w-full h-full flex items-center justify-center bg-[#0a0a0a] text-white"
    >
      <div className="w-full max-w-md px-4 text-center">
        <div className="mb-8">
          <img
            src="/SLAM_logo.png"
            alt="SLAM"
            className="w-16 h-16 mx-auto mb-4"
          />
          <h1 className="mb-2 text-2xl font-bold">Access Restricted</h1>
          <p
            className={`${"text-gray-300"
              } text-sm mb-6`}
          >
            You don't have permission to access any pages in the system. Please
            contact your administrator for access.
          </p>

          {/* Logout Button */}
          <button
            onClick={handleLogout}
            className="px-6 py-2 mb-6 font-medium text-white transition-all duration-200 rounded-lg shadow-md bg-gradient-to-r from-sky-500 to-sky-600 hover:from-sky-600 hover:to-sky-700 hover:shadow-lg"
          >
            Logout
          </button>
        </div>

        <div
          className={`p-4 rounded-lg ${"bg-gray-800 border border-gray-700"
            } shadow-sm`}
        >
          <p className="text-sm">
            If you believe this is an error, please contact your system
            administrator.
          </p>
        </div>
      </div>
    </div>
  );
};

// Main app wrapper to access location
function AppWrapper() {
  return (
    <Router>
      <MsalProvider>
        <AppContent />
        <Toaster />
      </MsalProvider>
    </Router>
  );
}

function AppContent() {
  const location = useLocation();
  const navigate = useNavigate();
  const [activeView, setActiveView] = useState("dashboard");
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const { theme } = useTheme();
  const isDarkMode = true; // OLED: always dark
  const path = location.pathname;
  const isLogin = path !== "/login" && path !== "/no-access" && !path.startsWith("/try") && !path.startsWith("/m/try") && path !== "/draft-day";
  let user = getUser()
  const [showLogo, setShowLogo] = useState(true)
  useEffect(() => {
    setNavigator(navigate);
    refreshToken();
  }, [navigate]);

  useEffect(() => {
    if (user?.id) {
      getUserSettings(user?.id)
      getAllSportsByUserId(user?.id)
    }
  }, [user?.id])
  // Set active view based on current path
  useEffect(() => {
    if (path === "/" || path === "/dashboard") setActiveView("dashboard");
    else if (path === "/slam-ai" || path === "/ask-slam-ai") setActiveView("slamAI");
    else if (path === "/roster") setActiveView("analytics");
    else if (path.startsWith("/player-roster") || path.startsWith("/player"))
      setActiveView("playerRoster");
    else if (path.startsWith("/my-team") || path.startsWith("/team-player-profile"))
      setActiveView('team-player')
    // Legacy root routes all redirect to /{sport}/* now — no activeView needed

  }, [location]);

  // Handle responsive sidebar behavior
  useEffect(() => {
    const handleResize = () => {
      // Only auto-close sidebar on small screens
      // This allows the collapse button to function properly on desktop
      if (window.innerWidth < 768) {
        setIsSidebarOpen(false);
      } else if (!localStorage.getItem("sidebarState")) {
        // Default to open on desktop only if user hasn't set a preference
        setIsSidebarOpen(true);
      }
    };

    // Check if we have a saved preference
    const savedState = localStorage.getItem("sidebarState");
    if (savedState) {
      setIsSidebarOpen(savedState === "open");
    }

    window.addEventListener("resize", handleResize);
    handleResize(); // Initial check

    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Check if we should show the debug view
  const urlParams = new URLSearchParams(window.location.search);
  const showDebug = urlParams.get("debug") === "true";

  // Add styles for the slider integration
  useEffect(() => {
    // Add CSS rules for slider behavior
    const style = document.createElement("style");
    style.innerHTML = `
      .sidebar-collapsed {
        transform: translateX(-100%);
      }
      .content-with-slider {
        margin-right: 40%; /* Adjust based on slider width */
      }
      @media (max-width: 768px) {
        .content-with-slider {
          margin-right: 70%; /* Wider margin on small screens */
        }
      }
    `;
    document.head.appendChild(style);

    return () => {
      document.head.removeChild(style);
    };
  }, []);

  return (
    <div
      className="App flex flex-col h-screen bg-black transition-colors duration-200"
    >
      {/* Hide header for bifurcated platform routes - they have their own full-height layouts */}
      {isLogin && !path.startsWith('/basketball') && !path.startsWith('/baseball') && !path.startsWith('/golf') && !path.startsWith('/soccer') && !path.startsWith('/hockey') && !path.startsWith('/football') && path !== '/select-sport' ? (
        <Header
          setShowLogo={setShowLogo}
          showLogo={showLogo}
          isSidebarOpen={isSidebarOpen}
          setIsSidebarOpen={setIsSidebarOpen}
        />
      ) : null}
      <div className={`flex flex-1 ${!isLogin ? 'min-h-screen' : ''}`}>
        {/* Hide main sidebar for bifurcated platform routes and sport selector - they have their own layouts */}
        {isLogin && !path.startsWith('/m') && !path.startsWith('/basketball') && !path.startsWith('/baseball') && !path.startsWith('/golf') && !path.startsWith('/soccer') && !path.startsWith('/hockey') && !path.startsWith('/football') && path !== '/select-sport' ? (
          <Sidebar
            setShowLogo={setShowLogo}
            showLogo={showLogo}
            activeView={activeView}
            setActiveView={setActiveView}
            isSidebarOpen={isSidebarOpen}
            setIsSidebarOpen={setIsSidebarOpen}
          />
        ) : null}
        <div
          className={`flex-1 ${path === '/select-sport' || path === '/loading' || path.startsWith('/m') || path.startsWith('/basketball') || path.startsWith('/baseball') || path.startsWith('/golf') || path.startsWith('/soccer') || path.startsWith('/hockey') || path.startsWith('/football') ? 'overflow-hidden' : 'overflow-y-auto'} overflow-x-hidden ${isLogin ? ` ${path === "/ask-slam-ai" || path === "/ai-assistant" || path.includes(`widget-dashboard/`) || path.includes(`custom-dashboards`) || path.startsWith('/m') || path.startsWith('/basketball') || path.startsWith('/baseball') || path.startsWith('/golf') || path.startsWith('/soccer') || path.startsWith('/hockey') || path.startsWith('/football') || path === '/select-sport' || path === '/loading' ? "p-0" : "p-3 md:p-5"}` : "p-0"}  bg-black text-white`}
          style={isLogin && path !== '/select-sport' && !path.startsWith('/m') && !path.startsWith('/basketball') && !path.startsWith('/baseball') && !path.startsWith('/golf') && !path.startsWith('/soccer') && !path.startsWith('/hockey') && !path.startsWith('/football') ? {
            // Logged in pages with header: constrain to content area below header
            maxHeight: 'var(--content-max-height)',
            minHeight: 0,
          } : {
            // Login/no-access pages AND full-screen pages (select-sport, loading, basketball, baseball): fill entire viewport
            height: '100vh',
            minHeight: '100vh',
          }}
          data-app-content
        >
          <Routes>
            {/* PLG Trial Mode — unauthenticated demo experience */}
            <Route path="/try/*" element={
              <Suspense fallback={<div className="flex items-center justify-center h-screen bg-black"><div className="w-6 h-6 border-2 border-[#7BAFD4] border-t-transparent rounded-full animate-spin" /></div>}>
                <TryRoutes />
              </Suspense>
            } />
            {/* PLG Mobile Trial — unauthenticated mobile demo */}
            <Route path="/m/try/*" element={
              <Suspense fallback={<div className="flex items-center justify-center h-screen bg-black"><div className="w-6 h-6 border-2 border-[#7BAFD4] border-t-transparent rounded-full animate-spin" /></div>}>
                <MobileTryRoutes />
              </Suspense>
            } />
            {/* Standalone analyzer for Flutter WebView embedding */}
            <Route path="/m/standalone/analyzer" element={
              <Suspense fallback={<div className="flex items-center justify-center h-screen bg-black"><div className="w-6 h-6 border-2 border-[#7BAFD4] border-t-transparent rounded-full animate-spin" /></div>}>
                <StandaloneAnalyzer />
              </Suspense>
            } />
            {/* Mobile endpoint — separate UI, same API, same auth */}
            <Route path="/m/*" element={
              <ProtectedRoute>
                <Suspense fallback={<div className="flex items-center justify-center h-screen bg-black"><div className="w-6 h-6 border-2 border-[#7BAFD4] border-t-transparent rounded-full animate-spin" /></div>}>
                  <MobileApp />
                </Suspense>
              </ProtectedRoute>
            } />
            <Route path="/login" element={<Login setShowLogo={setShowLogo} />} />
            {/* PLG Draft Day — gamified onboarding for new users */}
            <Route path="/draft-day" element={
              <ProtectedRoute>
                <Suspense fallback={<div className="flex items-center justify-center h-screen bg-black"><div className="w-6 h-6 border-2 border-[#7BAFD4] border-t-transparent rounded-full animate-spin" /></div>}>
                  <DraftDay />
                </Suspense>
              </ProtectedRoute>
            } />
            <Route path="/no-access" element={<NoAccessPage />} />
            {/* /dashboard → redirect to sport-specific canvas */}
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <LegacyAskRedirect />
                </ProtectedRoute>
              }
            />
            {/* my-dashboard route removed - consolidated into main dashboard */}
            {/* NIL Valuation Engine route removed - Task 4 Page Removals */}
            {/* Legacy /slam-ai → redirect to sport-specific ask-slam-ai */}
            <Route
              path="/slam-ai"
              element={
                <ProtectedRoute>
                  <SlamAIRedirect />
                </ProtectedRoute>
              }
            />
            {/* <Route
              path="/chat"
              element={
                <ProtectedRoute>
                  {" "}
                  <ChatInterface />{" "}
                </ProtectedRoute>
              }
            /> */}
            {/* ═══════════════════════════════════════════════════════════════════════════
                LEGACY ROOT ROUTES — all redirect to sport-specific equivalents
                These routes existed before multi-sport bifurcation
            ═══════════════════════════════════════════════════════════════════════════ */}
            <Route path="/source" element={<SportRedirect subpath="roster" />} />
            <Route path="/roster" element={<SportRedirect subpath="roster" />} />
            <Route path="/scouting" element={<SportRedirect subpath="scouting" />} />
            <Route path="/video-analytics" element={<SportRedirect subpath="video-analytics" />} />
            <Route path="/development" element={<SportRedirect subpath="development" />} />
            <Route path="/reports" element={<SportRedirect subpath="reports" />} />
            <Route path="/permissions" element={<Navigate to="/select-sport" replace />} />
            <Route path="/settings" element={<SportRedirect subpath="settings" />} />
            <Route path="/integrations" element={<SportRedirect subpath="integrations" />} />
            <Route path="/roster/:playerId" element={<SportRedirect subpath="roster" />} />
            <Route path="/roster/:playerId/analytics" element={<SportRedirect subpath="roster" />} />
            {/* Hardcoded profile routes removed in Phase 2 design cleanup
                Use dynamic routes instead:
                - /player/:playerId
                - /team-player-profile/:playerId
                - /player-roster/:playerId
            */}
            <Route
              path="/player-roster"
              element={
                <ProtectedRoute>
                  {" "}
                  <PlayerRoster />{" "}
                </ProtectedRoute>
              }
            />
            <Route
              path="/player-roster/:playerId"
              element={
                <ProtectedRoute>
                  {" "}
                  <PlayerProfile />{" "}
                </ProtectedRoute>
              }
            />
            <Route
              path="/my-team-player"
              element={
                <ProtectedRoute>
                  {" "}
                  <MyPlayerProfile />{" "}
                </ProtectedRoute>
              }
            />
            <Route
              path="/player/:playerId"
              element={
                <ProtectedRoute>
                  {" "}
                  <TeamPlayerProfile />{" "}
                </ProtectedRoute>
              }
            />
            <Route
              path="/team-player-profile/:playerId"
              element={
                <ProtectedRoute>
                  {" "}
                  <TeamPlayerProfileNew />{" "}
                </ProtectedRoute>
              }
            />
            <Route
              path="/team-record/:teamId"
              element={
                <ProtectedRoute>
                  {" "}
                  <TeamRecord />{" "}
                </ProtectedRoute>
              }
            />
            <Route
              path="/pre-college-player"
              element={
                <ProtectedRoute>
                  {" "}
                  <RecuritingPortal />{" "}
                </ProtectedRoute>
              }
            />
            {/* Widget Framework removed — external widget-server decommissioned */}

            {/* V2 Feature Routes (SLAM V2 Integration) */}
            {/* Sport selection / onboarding */}
            <Route
              path="/select-sport"
              element={
                <ProtectedRoute>
                  <TeamSelector />
                </ProtectedRoute>
              }
            />
            {/* Legacy sport selector route - redirects to team selector */}
            <Route
              path="/sport-selector"
              element={<Navigate to="/select-sport" replace />}
            />
            {/* Legacy /agent-universe → redirect to sport-specific canvas */}
            <Route
              path="/agent-universe"
              element={
                <ProtectedRoute>
                  <LegacyAskRedirect />
                </ProtectedRoute>
              }
            />
            <Route path="/video-analyzer" element={<SportRedirect subpath="video-analyzer" />} />
            {/* THE WIRE legacy paths → sport-specific */}
            <Route path="/the-wire-report" element={<Navigate to="/baseball/the-wire" replace />} />
            <Route path="/the-wire" element={<Navigate to="/baseball/the-wire" replace />} />
            {/* /tipping redirects to sport-specific The Wire */}
            <Route path="/tipping" element={<Navigate to="/baseball/the-wire" replace />} />
            {/* Pitch prediction → baseball-specific */}
            <Route path="/pitch-prediction" element={<Navigate to="/baseball/advanced-scouting" replace />} />
            <Route path="/pitch-prediction/:sourceId" element={<Navigate to="/baseball/advanced-scouting" replace />} />

            {/* ═══════════════════════════════════════════════════════════════════════════
                BROWN BEARS INTELLIGENCE DASHBOARD (standalone password-gated)
                Accessible at /mbbbrown - no main app login required
            ═══════════════════════════════════════════════════════════════════════════ */}
            <Route
              path="/mbbbrown"
              element={
                <PasswordGate gatePassword="jerry" gateStorageKey="slam-brown-intel-auth">
                  <BrownHarvardDashboard />
                </PasswordGate>
              }
            />

            {/* ═══════════════════════════════════════════════════════════════════════════
                SPURS FREE THROW BIOMECHANICS (standalone password-gated POC)
                Accessible at /spursft - no main app login required
            ═══════════════════════════════════════════════════════════════════════════ */}
            <Route
              path="/spursft"
              element={
                <PasswordGate gatePassword="baronbros" gateStorageKey="slam-spursft-auth">
                  <CS3sBiomechDashboard defaultTab="free-throws" />
                </PasswordGate>
              }
            />

            {/* ═══════════════════════════════════════════════════════════════════════════
                SPORT SECTIONS — Data-driven from src/config/sportRoutes.js
                Each sport gets common routes + sport-specific additions.
                Basketball also gets Baron Bros (password-gated demo dashboards).
            ═══════════════════════════════════════════════════════════════════════════ */}
            {SPORTS.map((sport) => (
              <Route
                key={sport}
                path={`/${sport}`}
                element={
                  <ProtectedRoute>
                    <SportLayout sport={sport} />
                  </ProtectedRoute>
                }
              >
                <Route index element={<Navigate to="canvas" replace />} />
                {renderSportRoutes(sport)}

                {/* Basketball-only: Baron Bros password-gated section */}
                {sport === 'basketball' && (
                  <>
                    <Route path="baron" element={<PasswordGate gatePassword="baronbros" gateStorageKey="slam-baron-auth"><Outlet /></PasswordGate>}>
                      <Route index element={<Navigate to="spurs-okc" replace />} />
                      <Route path="spurs-okc" element={<SpursOKCDashboard />} />
                      <Route path="cs3s-biomech" element={<CS3sBiomechDashboard />} />
                    </Route>
                    <Route path="spurs-okc" element={<Navigate to="/basketball/baron/spurs-okc" replace />} />
                    <Route path="cs3s-biomech" element={<Navigate to="/basketball/baron/cs3s-biomech" replace />} />
                    <Route path="agent-universe-planetary-demo" element={<Navigate to="/basketball/canvas" replace />} />
                    <Route path="system/planet-ion" element={<Navigate to="/basketball/canvas" replace />} />
                  </>
                )}
              </Route>
            ))}


            {/* Legacy root routes → sport-specific */}
            <Route path="/athlete-performance" element={<SportRedirect subpath="development" />} />
            <Route path="/statcast" element={<Navigate to="/baseball/statcast" replace />} />

            {/* Demo & custom-dashboards removed — mock data deprecated, widget-server decommissioned */}
            <Route path="/demo" element={<Navigate to="/baseball/the-wire" replace />} />
            <Route path="/demo/:team" element={<Navigate to="/baseball/the-wire" replace />} />
            <Route path="/custom-dashboards" element={<Navigate to="/select-sport" replace />} />

            {/* Legacy redirects */}
            <Route
              path="/ai-assistant"
              element={<Navigate to="/slam-ai" replace />}
            />

            {/* Legacy /user-management route - redirects to sport selection */}
            <Route
              path="/user-management"
              element={<Navigate to="/select-sport" replace />}
            />

            {/* Legacy AI Analysis Hub → sport selection */}
            <Route
              path="/ai-analysis-hub"
              element={<Navigate to="/select-sport" replace />}
            />

            <Route path="/ai-video-analyzer" element={<SportRedirect subpath="video-analyzer" />} />

            {/* Legacy routes → sport-specific redirects */}
            <Route path="/transfer-portal" element={<SportRedirect subpath="transfer-portal" />} />
            <Route path="/my-team" element={<SportRedirect subpath="roster" />} />

            {/* Workspace routes → settings (workspace is managed within sport context) */}
            <Route path="/workspace" element={<SportRedirect subpath="settings" />} />
            <Route path="/workspace/:id" element={<SportRedirect subpath="settings" />} />

            {/* Public invitation pages — keep as-is (no auth required) */}
            <Route path="/invite/:token" element={<InvitationAcceptPage />} />
            <Route path="/accept-invitation" element={<TeamInviteAcceptPage />} />

            {/* Legacy bookmark redirects — all chain to sport-specific */}
            <Route path="/ask-slam-ai" element={<SportRedirect subpath="ask-slam-ai" />} />
            <Route path="/ask-slam-ai-legacy" element={<SportRedirect subpath="ask-slam-ai" />} />
            <Route path="/permissions-legacy" element={<SportRedirect subpath="user-management" />} />
            <Route path="/agent-universe-legacy" element={<SportRedirect subpath="canvas" />} />
            {/* NIL valuation legacy redirect removed - Task 4 */}

            <Route path="/" element={<DefaultRoute />} />
          </Routes>
        </div>
      </div>
    </div>
  );
}

export default AppWrapper;
